import { useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { css } from "../../../../styled-system/css";
import { HStack, VStack } from "../../../../styled-system/jsx";
import { Button } from "../../../components/core/basic/Button";
import { TextField } from "../../../components/fields/base/TextField";
import { TextAreaField } from "../../../components/fields/base/TextAreaField";
import { Select, SelectItem } from "../../../components/fields/base/Select";
import {
  dataViewScopeSchema,
  dataViewVisibilitySchema,
  type DataViewScope,
  type DataViewVisibility,
} from "../../../zodios/apiDefinitions/api/dataViews";
import { useAppSelector } from "../../../redux/hooks";

export type DataViewFormValues = {
  name: string;
  description: string;
  scope: DataViewScope;
  visibility: DataViewVisibility;
};

type DataViewFormProps = {
  initial?: Partial<DataViewFormValues>;
  submitLabel: string;
  onSubmit: (values: DataViewFormValues) => void;
  onCancel: () => void;
  isSaving?: boolean;
  existingNames?: readonly string[];
};

export const DataViewForm = ({
  initial,
  submitLabel,
  onSubmit,
  onCancel,
  isSaving,
  existingNames,
}: DataViewFormProps) => {
  const { t } = useTranslation(["dataViews"]);
  const isStaff = useAppSelector((state) => state.user.is_staff);

  const scopeOptions = useMemo<{ value: DataViewScope; label: string }[]>(
    () =>
      dataViewScopeSchema.options.map((value) => ({
        value,
        label:
          value === "MARKETPLACE" ? t(($) => $.dataViews.Marketplace)
          : value === "ORGANIZATION" ? t(($) => $.dataViews.Organization)
          : t(($) => $.dataViews["All organizations"]),
      })),
    [t],
  );

  const visibilityOptions = useMemo<{ value: DataViewVisibility; label: string }[]>(
    () =>
      dataViewVisibilitySchema.options.map((value) => ({
        value,
        label:
          value === "USER" ? t(($) => $.dataViews["Only me"])
          : value === "STAFF" ? t(($) => $.dataViews.Staff)
          : t(($) => $.dataViews.Anyone),
      })),
    [t],
  );

  const visibleVisibilityOptions = visibilityOptions.filter(
    (option) => option.value !== "STAFF" || isStaff || initial?.visibility === "STAFF",
  );

  const { control, handleSubmit } = useForm<DataViewFormValues>({
    defaultValues: {
      name: initial?.name ?? "",
      description: initial?.description ?? "",
      scope: initial?.scope ?? "ORGANIZATION",
      visibility: initial?.visibility ?? "USER",
    },
  });

  const validateName = (value: string) => {
    const trimmed = value.trim();
    if (trimmed.length === 0) return t(($) => $.dataViews["Name is required"]);
    if (existingNames?.some((name) => name.toLowerCase() === trimmed.toLowerCase())) {
      return t(($) => $.dataViews["A view with this name already exists"]);
    }
    return true;
  };

  const submit = handleSubmit((values) => {
    onSubmit({
      name: values.name.trim(),
      description: values.description.trim(),
      scope: values.scope,
      visibility: values.visibility,
    });
  });

  return (
    <form onSubmit={submit}>
      <VStack gap="medium" alignItems="stretch" paddingX="xlarge" paddingY="large">
        <Controller
          name="name"
          control={control}
          rules={{ validate: validateName }}
          render={({ field, fieldState }) => (
            <TextField
              label={t(($) => $.dataViews.Name)}
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              errorMessage={fieldState.error?.message}
              autoFocus
              isRequired
            />
          )}
        />
        <Controller
          name="description"
          control={control}
          render={({ field }) => (
            <TextAreaField
              label={t(($) => $.dataViews.Description)}
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
            />
          )}
        />
        <HStack gap="medium" alignItems="start">
          <Controller
            name="scope"
            control={control}
            render={({ field }) => (
              <Select<{ value: DataViewScope; label: string }>
                label={t(($) => $.dataViews.Scope)}
                items={scopeOptions}
                selectedKey={field.value}
                onSelectionChange={(key) => field.onChange(key)}
                onBlur={field.onBlur}
                css={{ flex: 1 }}
              >
                {(item) => (
                  <SelectItem id={item.value} textValue={item.label}>
                    {item.label}
                  </SelectItem>
                )}
              </Select>
            )}
          />
          <Controller
            name="visibility"
            control={control}
            render={({ field }) => (
              <Select<{ value: DataViewVisibility; label: string }>
                label={t(($) => $.dataViews["Visible to"])}
                items={visibleVisibilityOptions}
                selectedKey={field.value}
                onSelectionChange={(key) => field.onChange(key)}
                onBlur={field.onBlur}
                css={{ flex: 1 }}
              >
                {(item) => (
                  <SelectItem id={item.value} textValue={item.label}>
                    {item.label}
                  </SelectItem>
                )}
              </Select>
            )}
          />
        </HStack>
        <HStack justifyContent="space-around" paddingTop="medium" className={css({ width: "100%" })}>
          <Button variant="secondary" onPress={onCancel} isDisabled={isSaving}>
            {t(($) => $.dataViews.Cancel)}
          </Button>
          <Button type="submit" isDisabled={isSaving}>
            {isSaving ? t(($) => $.dataViews["Saving…"]) : submitLabel}
          </Button>
        </HStack>
      </VStack>
    </form>
  );
};
